// Ubicación en el repo: src/pages/Branches.jsx
// Administración de Sucursales (spec-prototipo.md 1.9). Solo Admin/
// bootstrap llega acá (ver guard en App.jsx) — RLS (`branches_write`)
// también lo exige del lado del servidor, esto es solo UX.
import { useCallback, useEffect, useState } from 'react';
import { listAllBranchesForAdmin, softDeleteBranch, restoreBranch } from '../lib/branches';
import { useOutletContext } from 'react-router-dom';
import BranchFormModal from '../components/BranchFormModal';
import Spinner from '../components/Spinner';

export default function Branches() {
  const { professional, refreshBranches } = useOutletContext();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setBranches(await listAllBranchesForAdmin());
    } catch (err) {
      setError(err.message || 'No se pudo cargar el listado de sucursales.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  function handleNew() {
    setEditTarget(null);
    setFormOpen(true);
  }

  function handleEdit(branch) {
    setEditTarget(branch);
    setFormOpen(true);
  }

  async function handleToggleActive(branch) {
    const action = branch.deleted_at ? 'reactivar' : 'desactivar';
    const ok = window.confirm(`¿${action === 'reactivar' ? 'Reactivar' : 'Desactivar'} "${branch.name}"?`);
    if (!ok) return;
    try {
      if (branch.deleted_at) {
        await restoreBranch(branch.id);
      } else {
        await softDeleteBranch(branch.id, professional.id);
      }
      refresh();
      refreshBranches(); // ver comentario en Layout.jsx — el header tiene su propia copia
    } catch (err) {
      setError(err.message || `No se pudo ${action} la sucursal.`);
    }
  }

  function handleFormSaved() {
    setFormOpen(false);
    setEditTarget(null);
    refresh();
    refreshBranches(); // ver comentario en Layout.jsx — el header tiene su propia copia
  }

  // Guard de UX — RLS (`branches_write`) es la protección real, ver mismo
  // comentario en Professionals.jsx.
  if (!professional.is_admin) {
    return (
      <div className="patients-page">
        <h1>Sucursales</h1>
        <p className="text-muted">Solo un Admin puede gestionar sucursales.</p>
      </div>
    );
  }

  return (
    <div className="patients-page">
      <div className="patients-page__toolbar">
        <h1>Sucursales</h1>
        <div className="patients-page__toolbar-right">
          <button type="button" className="button-primary" onClick={handleNew}>
            + Nueva sucursal
          </button>
        </div>
      </div>

      {error && <p className="auth-error">{error}</p>}

      <table className="patients-table">
        <thead>
          <tr>
            <th>Nombre</th>
            <th>Dirección</th>
            <th>Teléfono</th>
            <th>Logo</th>
            <th>Módulos</th>
            <th>Estado</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={7}><Spinner inline /></td>
            </tr>
          ) : branches.length === 0 ? (
            <tr>
              <td colSpan={7}>No hay sucursales registradas todavía.</td>
            </tr>
          ) : (
            branches.map((b) => (
              <tr key={b.id}>
                <td>{b.name}</td>
                <td>{b.address || '—'}</td>
                <td>{b.phone || '—'}</td>
                <td>{b.logo_path ? <span className="badge badge-ok">Sí</span> : <span className="text-muted">No</span>}</td>
                <td>
                  {b.orl_enabled !== false && <span className="badge badge-neutral">ORL</span>}{' '}
                  {b.oft_enabled !== false && <span className="badge badge-neutral">OFT</span>}
                </td>
                <td>
                  {b.deleted_at ? (
                    <span className="badge badge-danger">Inactiva</span>
                  ) : (
                    <span className="badge badge-ok">Activa</span>
                  )}
                </td>
                <td className="actions-cell">
                  <button type="button" className="link-button" onClick={() => handleEdit(b)}>
                    Editar
                  </button>
                  <button
                    type="button"
                    className={`link-button ${b.deleted_at ? '' : 'link-button--danger'}`}
                    onClick={() => handleToggleActive(b)}
                  >
                    {b.deleted_at ? 'Reactivar' : 'Desactivar'}
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {formOpen && (
        <BranchFormModal
          branch={editTarget}
          onClose={() => {
            setFormOpen(false);
            setEditTarget(null);
          }}
          onSaved={handleFormSaved}
        />
      )}
    </div>
  );
}
